import React, { useEffect, useState } from 'react';

/**
 * `CustomerVehicleDetailsPage` captures and/or loads vehicle details for the
 * customer journey.
 *
 * Responsibilities:
 * - Accepts navigation state for existing quotes, a preloaded vehicle, or an
 *   id value (VRN/VIN) and derives the appropriate lookup behaviour.
 * - Loads vehicle details via `VehicleApi` for VRN/VIN lookups, applying
 *   journey/CMS rules (e.g. overseas behaviour, VIN input forcing).
 * - Updates journey state with loaded vehicle details and derives default GAP
 *   introduction date when GAP is available.
 * - Coordinates validation/update cycles with `VehiclePage` and navigates to
 *   products on success.
 */

import { useLocation, useNavigate } from 'react-router-dom';
import {
  VehicleType,
  VehicleState,
  QuotesData,
  VinWarrantyExpiry
} from '../../common/CommonTypes';
import { VehicleApi } from '../../../api/VehicleApi';
import {
  PageAbsolutePath,
  PageRelativePath,
  ProductTypes,
  VehicleIdType
} from '../../../consts/Common';
import VehiclePage from '../../common/VehiclePage/VehiclePage';
import PageHeader from '../../common/PageHeader';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useJourney } from './../Layout';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import ClientDateHandler from '../../common/ClientDateHandler/ClientDateHandler';
import {
  setCountryCode,
  isMandatoryDealerReference
} from '../../common/HelperMethods';
import { blankVehicle } from '../../testData/journeyHelper';
import { CountryIsoCodes } from '../../common/Constants';
import { Helmet } from 'react-helmet';
import { useAppSelector } from '../../../app-state/Hooks';
export type LocationQuote = {
  quote: QuotesData;
};

export type LocationVehicle = {
  vehicle: VehicleType;
};

export type LocationVehicleId = {
  idValue: string;
  vehicleIdType: VehicleIdType;
  expiryDetails?: VinWarrantyExpiry;
};

type LocationState = LocationQuote | LocationVehicle | LocationVehicleId;

const initialVehicleDetails: VehicleType = blankVehicle;

const CustomerVehicleDetailsPage = (): JSX.Element => {
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [updateVehicleDetails, setUpdateVehicleDetails] = useState<
    boolean | null
  >(null);
  const [validateVehicleDetails, setValidateVehicleDetails] = useState<
    boolean | null
  >(null);
  const [goForward, setGoForward] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [identificationNumber, setIdentificationNumber] = useState('');

  const {
    journey,
    onUpdateVehicleDetails,
    onUpdateProducts,
    onUpdateProductDocuments,
    onPageChange,
    onUpdateFoundExistingQuotes,
    setQuoteId,
    onUpdateQuotedProducts,
    setPersonalDetails,
    setExceedsUpsellMileageLimit,
    setExpiryDetails
  } = useJourney();

  const { vehicle, exceedsUpsellMileageLimit, isOpensiteUser, expiryDetails } =
    journey;
  const { vehicleDetails } = vehicle;

  const navigate = useNavigate();

  const { state } = useLocation();

  const user = useAppSelector((s) => s.user);
  const appConfig = useAppSelector((s) => s.appConfig);

  const cms = useAppSelector((s) => s.content.vehiclePage);
  const landingCms = useAppSelector((s) => s.content.landingPage);
  const productTypeCustomisation = useAppSelector(
    (s) => s.content.productPage.productTypeCustomisation
  );

  const isCallCentreUser = !!user?.userId;
  const {
    hideDealerReference,
    hideDealerReferenceCCI,
    hidePurchasePrice,
    hideMileage,
    hideDeliveryDate,
    externalReferenceLabelText,
    hideVinInput,
    mandatoryExternalReference,
    mandatoryExternalReferenceCCI
  } = cms;

  const { useVinInput, forceVinCheck } = landingCms;

  const showDealerReference =
    (isOpensiteUser && !hideDealerReference) ||
    (isCallCentreUser && !hideDealerReferenceCCI);

  const mandatoryDealerReference = isMandatoryDealerReference(
    showDealerReference,
    isCallCentreUser,
    mandatoryExternalReferenceCCI,
    mandatoryExternalReference
  );

  const showVehiclePurchasePrice = !appConfig.quoteUpsell && !hidePurchasePrice;
  const showVehicleDeliveryDate = !appConfig.quoteUpsell && !hideDeliveryDate;
  const [quotes, setQuotes] = useState<QuotesData | undefined>(undefined);

  const showEditVrn = isOpensiteUser || isCallCentreUser;
  const showEditVehicle = isOpensiteUser || useVinInput;

  const { t } = useTranslation();
  const overseasWebsite = appConfig.countryCode !== CountryIsoCodes.GB;

  const determineIfQuotePassed = (
    toBeDetermined: LocationState
  ): toBeDetermined is LocationQuote => {
    if ((toBeDetermined as LocationQuote).quote) {
      return true;
    }
    return false;
  };

  const determineIfExpiryDetailsPassed = (
    toBeDetermined: LocationState
  ): toBeDetermined is LocationVehicleId => {
    if ((toBeDetermined as LocationVehicleId).expiryDetails) {
      return true;
    }
    return false;
  };

  const determineIfVehiclePassed = (
    toBeDetermined: LocationState
  ): toBeDetermined is LocationVehicle => {
    if ((toBeDetermined as LocationVehicle).vehicle) {
      return true;
    }
    return false;
  };

  const determineIfVehicleIdPassed = (
    toBeDetermined: LocationState
  ): toBeDetermined is LocationVehicleId => {
    if ((toBeDetermined as LocationVehicleId).idValue) {
      return true;
    }
    return false;
  };

  const receivedExpiryDetails = expiryDetails ? true : false;

  const vehicleDeliveryDateEditable =
    isOpensiteUser || isCallCentreUser || receivedExpiryDetails;

  useEffect(() => {
    if (!state) {
      return;
    }

    const hasQuote = determineIfQuotePassed(state as LocationState);

    if (hasQuote) {
      const quote = (state as LocationQuote).quote;

      updateJourneyStateFromQuoteData(quote);
      setQuotes(quote);
      return;
    }

    const hasVehicle = determineIfVehiclePassed(state as LocationState);

    if (hasVehicle) {
      const locationVehicleDetails = (state as LocationVehicle).vehicle;

      onUpdateVehicleDetails({
        ...vehicle,
        vehicleDetails: locationVehicleDetails
      });

      return;
    }

    const hasVehicleId = determineIfVehicleIdPassed(state as LocationState);

    if (hasVehicleId) {
      const vehicleIdState = state as LocationVehicleId;

      switch (vehicleIdState.vehicleIdType) {
        case VehicleIdType.Vrn:
          setRegistrationNumber(vehicleIdState.idValue);
          break;
        case VehicleIdType.Vin:
          setIdentificationNumber(vehicleIdState.idValue);
          break;
      }
    }

    const hasExpiryDetails = determineIfExpiryDetailsPassed(
      state as LocationState
    );
    if (hasExpiryDetails) {
      const vehicleIdState = state as LocationVehicleId;
      if (setExpiryDetails && vehicleIdState && vehicleIdState.expiryDetails)
        setExpiryDetails(vehicleIdState.expiryDetails);
    }

    return;
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchVehicle = () => {
      fetchVehicleByVrn(registrationNumber, controller.signal);
    };

    fetchVehicle();

    return () => {
      controller.abort();
    };
  }, [registrationNumber]);

  useEffect(() => {
    if (CommonValidation.isNotEmpty(identificationNumber)) {
      if (
        (!hideVinInput && !appConfig.includeQuotes && !useVinInput) ||
        overseasWebsite
      ) {
        const errorMessage = t(
          useVinInput && forceVinCheck
            ? 'common-error-msg.api-error-msg.vehicle-vin-input-error'
            : 'common-error-msg.api-error-msg.vehicle-vin-error'
        );
        const vehicleDetails: VehicleType = {
          ...vehicle.vehicleDetails,
          vin: identificationNumber,
          errorMessage: overseasWebsite ? errorMessage : '',
          errorOrigin: 'vin'
        };

        onUpdateVehicleDetails({
          ...vehicle,
          vehicleDetails: vehicleDetails
        });
      } else {
        const controller = new AbortController();

        const fetchVehicle = () => {
          fetchVehicleByVin(identificationNumber, controller.signal);
        };

        fetchVehicle();

        return () => {
          controller.abort();
        };
      }
    }
  }, [identificationNumber]);

  useEffect(() => {
    if (goForward === true && updateVehicleDetails === false) {
      navigate(PageRelativePath.Products, {
        state: {
          quote: quotes,
          expiryDetails: (state as LocationVehicleId)?.expiryDetails,
          vehicleIdType: getVehicleIdType()
        }
      });
      onPageChange(PageAbsolutePath.Customer.VehicleDetails, true);
    }
  }, [updateVehicleDetails]);

  const getVehicleIdType = () => {
    if (state) {
      return (state as LocationVehicleId)?.vehicleIdType;
    } else if (vehicle?.vehicleDetails?.vrn) {
      return VehicleIdType.Vrn;
    } else if (vehicle?.vehicleDetails?.vin) {
      return VehicleIdType.Vin;
    }
  };

  const fetchVehicleByVrn = async (reg: string, signal: AbortSignal) => {
    if (
      overseasWebsite &&
      CommonValidation.isEmpty(registrationNumber) &&
      (vehicleDetails.registrationDate === '' ||
        vehicleDetails.registrationDate === null)
    ) {
      const userVehicle = initialVehicleDetails;
      userVehicle.errorOrigin = 'vrn';
      userVehicle.errorMessage = '';
      userVehicle.vrn = '';
      userVehicle.vin = identificationNumber;
      updateLoadedVehicle(userVehicle);
    }
    if (CommonValidation.isNotEmpty(registrationNumber)) {
      if (overseasWebsite) {
        const updatedVehicle: VehicleType = {
          ...vehicle.vehicleDetails,
          vrn: reg
        };

        updateLoadedVehicle(updatedVehicle);
      } else {
        setLoadingVehicle(true);

        await new VehicleApi().getRegisteredVehicles(
          reg,
          user.token,
          (data) => {
            fetchVehicleSuccessVrn(data);
            setLoadingVehicle(false);
          },
          () => {
            fetchVehicleFailureVrn();
            setLoadingVehicle(false);
          },
          signal
        );
      }
    }
  };

  const fetchVehicleByVin = async (id: string, signal: AbortSignal) => {
    setLoadingVehicle(true);

    await new VehicleApi().getRegisteredVehiclesVin(
      id,
      user.token,
      (data) => {
        fetchVehicleSuccessVin(data);
        setLoadingVehicle(false);
      },
      () => {
        fetchVehicleFailureVin();
        setLoadingVehicle(false);
      },
      signal,
      false
    );
  };

  const fetchVehicleSuccessVin = (userVehicle: VehicleType) => {
    if (
      userVehicle.registrationDate === '' ||
      userVehicle.registrationDate === null
    ) {
      userVehicle.errorOrigin = 'vin';
      userVehicle.errorMessage = t(
        useVinInput && forceVinCheck
          ? 'common-error-msg.api-error-msg.vehicle-vin-input-error'
          : 'common-error-msg.api-error-msg.vehicle-vin-error'
      );
      userVehicle.vin = identificationNumber;
    }

    updateLoadedVehicle(userVehicle);
  };

  const fetchVehicleSuccessVrn = (userVehicle: VehicleType) => {
    console.log('testingvrn-succcess');
    if (
      userVehicle.registrationDate === '' ||
      userVehicle.registrationDate === null
    ) {
      userVehicle.errorOrigin = 'vrn';
      userVehicle.errorMessage = overseasWebsite
        ? ''
        : t('common-error-msg.api-error-msg.vehicle-vreg-error');
      userVehicle.vrn = registrationNumber;
      if (
        overseasWebsite &&
        CommonValidation.isNotEmpty(identificationNumber)
      ) {
        //
        userVehicle.vin = identificationNumber;
      }
    }

    if (!forceVinCheck || CommonValidation.isNotEmpty(userVehicle.vin)) {
      updateLoadedVehicle(userVehicle);
    } else {
      const existingVehicle = {
        ...journey.vehicle.vehicleDetails,
        errorOrigin: userVehicle.errorOrigin,
        errorMessage: userVehicle.errorMessage
      };

      updateLoadedVehicle(existingVehicle);
    }
  };

  const setGapDate = () => {
    let gapDate = undefined;

    if (
      vehicle &&
      productTypeCustomisation.some((p) => p.productType === ProductTypes.Gap)
    ) {
      const date = ((d) => new Date(d.setDate(d.getDate() - 1)))(new Date());

      const cdh = new ClientDateHandler();

      cdh.setDateValue(false, date);

      gapDate = cdh.getJSON();
    }

    return gapDate;
  };

  const updateLoadedVehicle = (userVehicle: VehicleType) => {
    const gapDate = setGapDate();

    onUpdateVehicleDetails({
      ...journey.vehicle,
      vehicleDetails: userVehicle,
      vehicleGapIntroductionDate: gapDate
    });
  };

  const fetchVehicleFailureVin = () => {
    const userVehicle = initialVehicleDetails;

    userVehicle.errorOrigin = 'vin';
    userVehicle.errorMessage = t(
      'common-error-msg.api-error-msg.vehicle-vin-error'
    );
    userVehicle.vin = identificationNumber;

    updateLoadedVehicle(userVehicle);
  };

  const fetchVehicleFailureVrn = () => {
    const userVehicle = initialVehicleDetails;

    userVehicle.errorOrigin = 'vrn';
    userVehicle.errorMessage = overseasWebsite
      ? ''
      : t('common-error-msg.api-error-msg.vehicle-vreg-error');
    userVehicle.vrn = registrationNumber;

    updateLoadedVehicle(userVehicle);
  };

  const updateJourneyStateFromQuoteData = (quoteData: QuotesData) => {
    if (!quoteData) {
      if (onUpdateFoundExistingQuotes) {
        onUpdateFoundExistingQuotes(false);
      }
      return;
    }

    if (
      CommonValidation.isNotEmpty(
        quoteData.vehicleInfo.vehicleDetails.registrationDate
      )
    ) {
      if (!overseasWebsite && CommonValidation.isNotEmpty(registrationNumber)) {
        quoteData.vehicleInfo.vehicleDetails.errorOrigin = 'vrn';
        quoteData.vehicleInfo.vehicleDetails.errorMessage = t(
          'common-error-msg.api-error-msg.quote-vreg-error'
        );
      } else if (CommonValidation.isNotEmpty(identificationNumber)) {
        quoteData.vehicleInfo.vehicleDetails.errorOrigin = 'vin';
        quoteData.vehicleInfo.vehicleDetails.errorMessage = t(
          'common-error-msg.api-error-msg.quote-vin-error'
        );
      } else {
        quoteData.vehicleInfo.vehicleDetails.errorOrigin = 'email';
        quoteData.vehicleInfo.vehicleDetails.errorMessage = t(
          'common-error-msg.api-error-msg.quote-email-error'
        );
      }
    }

    if (!quoteData.vehicleInfo.vehicleGapIntroductionDate) {
      const gapDate = setGapDate();

      onUpdateVehicleDetails({
        ...quoteData.vehicleInfo,
        vehicleGapIntroductionDate: gapDate
      });
    } else {
      onUpdateVehicleDetails(quoteData.vehicleInfo);
    }
    if (quoteData.products && quoteData.products.products) {
      onUpdateProducts(quoteData.products.products);
    }
    if (quoteData.products && quoteData.products.documents) {
      onUpdateProductDocuments(quoteData.products.documents);
    }
    onUpdateQuotedProducts(quoteData.quotedProducts);
    setPersonalDetails(quoteData.personalDetail);
    if (onUpdateFoundExistingQuotes) {
      onUpdateFoundExistingQuotes(true);
    }

    if (setQuoteId) {
      setQuoteId(quoteData.id);
    }

    navigate(PageRelativePath.VehicleDetails, {
      state: {
        quote: quotes
      }
    });

    onPageChange(PageAbsolutePath.Customer.VehicleDetails, true);
  };

  const goToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const onBack = () => {
    //TODO warn that the details entered will be lost
    onPageChange(PageAbsolutePath.Customer.VehicleDetails, false);
    const path = setCountryCode(
      PageAbsolutePath.Customer.Landing,
      appConfig.countryCode
    );
    navigate(path);
    goToTop();
  };

  const onSeeProductsClick = () => {
    setValidateVehicleDetails(true);
    setGoForward(true);
  };

  const vehicleNotFound =
    loadingVehicle ||
    vehicleDetails.registrationDate === '' ||
    vehicleDetails.registrationDate === null;

  const onUpdateVehicleState = (vs: VehicleState) => {
    onUpdateVehicleDetails(vs);
    setUpdateVehicleDetails(false);
  };

  const onVehicleValidated = (isValid: boolean) => {
    if (isValid && goForward) setUpdateVehicleDetails(true);
    setValidateVehicleDetails(false);
  };

  const showIsDeliveryDateEstimated =
    appConfig.includeQuotes &&
    !appConfig.quoteUpsell &&
    !cms.hideEstimatedDeliveryDate;

  const isEditable = (isCmsFieldEditable: boolean) => {
    if (isOpensiteUser) {
      return true;
    } else {
      return isCmsFieldEditable;
    }
  };

  const isReadOnly =
    (!isOpensiteUser && !receivedExpiryDetails) || forceVinCheck;

  return (
    <LoadingContainer
      loading={loadingVehicle}
      message={t('loading-placeholder.loading-text')}>
      <Helmet>
        <title>{t('page-title.vehicle-details')}</title>
      </Helmet>
      <div>
        <div>
          <PageHeader
            title={cms.title}
            description={cms.description}
            information={cms.information}
            warning={cms.warning}
            descriptionClass='p2'
          />
          <VehiclePage
            initialVehicleInfo={vehicle}
            update={updateVehicleDetails ?? false}
            validate={validateVehicleDetails ?? false}
            onValidated={onVehicleValidated}
            onUpdateVehicleState={onUpdateVehicleState}
            onUpdateRegistrationNumber={(vrn) => setRegistrationNumber(vrn)}
            showEditVrn={showEditVrn ?? false}
            showGapIntroductionDate={false}
            showModelTrim={false}
            showPolicyStartDate={false}
            isPolicyStartDateEnabled={false}
            showSalesId={false}
            showVehicleDeliveryDate={showVehicleDeliveryDate ?? false}
            showDealerReference={showDealerReference ?? false}
            showFieldsOnDataError={true}
            vehiclePurchasePriceLabel={t(
              'customer-vehicle-details-page.vehicle-purchase-price-label'
            )}
            token={user.token}
            showVin={appConfig.includeQuotes || useVinInput || !hideVinInput}
            showIsDeliveryDateEstimated={showIsDeliveryDateEstimated}
            customisation={cms}
            onUpdateIdentificationNumber={(vin) => setIdentificationNumber(vin)}
            isReadOnly={isReadOnly}
            futureDateAllowed={isOpensiteUser ?? false}
            showNeedHelpTooltipDeliveryDate={false}
            showVehiclePurchasePrice={showVehiclePurchasePrice ?? false}
            upsellAnnualMileageText={cms.upsellAnnualMileage}
            exceedsUpsellMileageLimit={exceedsUpsellMileageLimit}
            setExceedsUpsellMileageLimit={setExceedsUpsellMileageLimit}
            showEditVehicle={showEditVehicle ?? false}
            quoteUpsell={appConfig.quoteUpsell}
            showMileage={!hideMileage}
            isPurchasePriceEditable={isEditable(cms.editablePurchasePrice)}
            isMileageEditable={isEditable(cms.editableMileage)}
            isSalesIdMandatory={false}
            culture={appConfig.cultureCode}
            isVehicleDeliveryEditable={vehicleDeliveryDateEditable}
            ignoreVinFromManualDetails={useVinInput || cms.mandatoryVin}
            externalReferenceLabelText={externalReferenceLabelText}
            expiryDetails={expiryDetails}
            isDealerReferenceMandatory={mandatoryDealerReference}
          />
          <div className='flex-row form-nav mobile-width-90pc'>
            {!isCallCentreUser && (
              <ButtonBack
                name='btnBack'
                onClick={onBack}
                text={t('customer-vehicle-details-page.back-button-text')}
              />
            )}
            <ButtonContinue
              name='btn-see-products'
              text={t('customer-vehicle-details-page.continue-button-text')}
              onClick={onSeeProductsClick}
              disabled={vehicleNotFound}
            />
          </div>
        </div>
      </div>
    </LoadingContainer>
  );
};

export default CustomerVehicleDetailsPage;
